import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Table, Alert, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';

/**
 * TIN-23 P1+P2: Modal de confirmación de correos antes de enviar el link
 * de cotización a uno o varios proveedores. El usuario puede editar el
 * correo destino por si el que está en BD no es el correcto.
 *
 * Props:
 * - show: boolean
 * - onHide: () => void
 * - titulo: string (ej "Enviar a 3 proveedores pendientes")
 * - descripcion: string opcional
 * - proveedoresAsignados: array de PA precargados, cada uno con:
 *     { _id, proveedor: { nombre, email }, estadoEnvio, correoEnvioInvitacion? }
 * - confirmarLabel: string (default "Enviar")
 * - onConfirmar: async (envios: [{ proveedorAsignadoId, correoOverride }]) => void
 */
const ConfirmarEnvioEmailModal = ({
  show,
  onHide,
  titulo = 'Confirmar envío',
  descripcion,
  proveedoresAsignados = [],
  confirmarLabel = 'Enviar',
  onConfirmar
}) => {
  const [correos, setCorreos] = useState({}); // { [paId]: 'email' }
  const [enviando, setEnviando] = useState(false);

  // Precarga inicial: usa correoEnvioInvitacion guardado o el email base del proveedor.
  useEffect(() => {
    if (!show) return;
    const inicial = {};
    proveedoresAsignados.forEach((pa) => {
      inicial[pa._id] = (pa.correoEnvioInvitacion || pa.proveedor?.email || '').trim();
    });
    setCorreos(inicial);
  }, [show, proveedoresAsignados]);

  const cambiarCorreo = (paId, valor) => {
    setCorreos((prev) => ({ ...prev, [paId]: valor }));
  };

  const validar = () => {
    const reEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidos = proveedoresAsignados
      .map((pa) => ({ pa, correo: (correos[pa._id] || '').trim() }))
      .filter(({ correo }) => !reEmail.test(correo));
    return invalidos;
  };

  const envios = useMemo(
    () => proveedoresAsignados.map((pa) => ({
      proveedorAsignadoId: pa._id,
      correoOverride: (correos[pa._id] || '').trim()
    })),
    [proveedoresAsignados, correos]
  );

  const handleConfirmar = async () => {
    const invalidos = validar();
    if (invalidos.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Correo inválido',
        text: invalidos.length === 1
          ? `El correo de "${invalidos[0].pa.proveedor?.nombre || 'proveedor'}" no es válido.`
          : `Hay ${invalidos.length} correos inválidos. Revísalos antes de continuar.`
      });
      return;
    }

    setEnviando(true);
    try {
      await onConfirmar?.(envios);
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error al enviar',
        text: e?.response?.data?.message || e?.message || 'No se pudo completar el envío.'
      });
    } finally {
      setEnviando(false);
    }
  };

  const handleHide = () => {
    if (enviando) return;
    onHide?.();
  };

  return (
    <Modal show={show} onHide={handleHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton={!enviando}>
        <Modal.Title>{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {descripcion && <p className="text-muted small mb-3">{descripcion}</p>}
        {proveedoresAsignados.length === 0 ? (
          <Alert variant="warning" className="mb-0">
            No hay proveedores para enviar.
          </Alert>
        ) : (
          <Table responsive hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Proveedor</th>
                <th>Estado</th>
                <th style={{ minWidth: 260 }}>Correo destino (editable)</th>
              </tr>
            </thead>
            <tbody>
              {proveedoresAsignados.map((pa) => (
                <tr key={pa._id}>
                  <td>
                    <strong>{pa.proveedor?.nombre || '—'}</strong>
                    {pa.proveedor?.nit && (
                      <div><small className="text-muted">NIT: {pa.proveedor.nit}</small></div>
                    )}
                  </td>
                  <td>
                    <small className="text-muted">{pa.estadoEnvio}</small>
                  </td>
                  <td>
                    <Form.Control
                      type="email"
                      size="sm"
                      value={correos[pa._id] || ''}
                      onChange={(e) => cambiarCorreo(pa._id, e.target.value)}
                      placeholder="correo@dominio.com"
                      disabled={enviando}
                    />
                    {pa.proveedor?.email && correos[pa._id] && correos[pa._id].trim() !== pa.proveedor.email && (
                      <small className="text-warning">
                        ⚠ Distinto al registrado: <code>{pa.proveedor.email}</code>
                      </small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <small className="text-muted">
          El correo editado se guarda con el proveedor asignado para futuros reenvíos
          y queda registrado en el timeline.
        </small>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={enviando}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmar}
          disabled={enviando || proveedoresAsignados.length === 0}
        >
          {enviando
            ? <><Spinner animation="border" size="sm" className="me-1" />Enviando...</>
            : <><i className="bi bi-send me-1"></i>{confirmarLabel}</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmarEnvioEmailModal;
